import { useEffect } from 'react';

interface LockedElement {
  element: HTMLElement;
  originalOverflow: string;
}

/**
 * Hook que bloquea el scroll del body y opcionalmente de elementos adicionales.
 * Usa estilos inline para garantizar prioridad sobre cualquier regla CSS.
 * Restaura el overflow original automáticamente cuando el componente se desmonta o cuando isLocked cambia a false.
 * @param isLocked - Indica si el scroll debe estar bloqueado
 * @param additionalSelectors - Array opcional de selectores CSS de elementos adicionales a los que bloquear el scroll.
 * Pueden ser IDs (ej: '#miElemento'), clases (ej: '.miClase') o cualquier selector CSS válido.
 */
const useLockScroll = (isLocked: boolean, additionalSelectors?: string[]): void => {
  const OVERFLOW_HIDDEN_VALUE = 'hidden';

  useEffect(() => {
    if (!isLocked) return;

    const originalBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = OVERFLOW_HIDDEN_VALUE;

    const lockedElements: LockedElement[] = [];

    if (additionalSelectors?.length) {
      additionalSelectors.forEach(selector => {
        const isIdWithoutHash = !selector.startsWith('#') && !selector.startsWith('.') && !selector.includes(' ');
        const finalSelector = isIdWithoutHash ? `#${selector}` : selector;

        const elements = document.querySelectorAll<HTMLElement>(finalSelector);
        elements.forEach(element => {
          lockedElements.push({
            element,
            originalOverflow: element.style.overflow
          });
          element.style.overflow = OVERFLOW_HIDDEN_VALUE;
        });
      });
    }

    return () => {
      document.body.style.overflow = originalBodyOverflow;

      lockedElements.forEach(({ element, originalOverflow }) => {
        element.style.overflow = originalOverflow;
      });
    };
  }, [isLocked, additionalSelectors]);
};

export default useLockScroll;
